<script>
import Spinner from './Spinner.vue';

export default {
  name: 'Button',
  components: {
    Spinner
  },
  props: {
    label: {
      type: String,
      required: true
    },
    color: {
      type: String,
      required: true,
      validator: (value) => ['gold', 'black', 'white', 'maroon'].includes(value)
    },
    loading: {
      type: Boolean,
      default: false
    },
    isSubmit: {
      type: Boolean,
      default: false
    },
    href: {
      type: String,
      default: null
    },
    newTab: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isDisabled() {
      return this.disabled || this.loading || (this.isSubmit && !this.label);
    }
  },
  methods: {
    handleClick() {
      if (this.disabled) return;
      this.$emit('click');
    }
  }
};
</script>

<template>
  <component
    :is="href ? 'a' : 'button'"
    class="btn"
    @click="handleClick"
    :class="color"
    v-bind="href ? {} : { disabled: isDisabled }"
    :aria-busy="loading"
    :href="isDisabled ? undefined : href"
    :target="newTab ? '_blank' : undefined"
    style="text-decoration: none"
  >
    <slot name="start-icon" />
    <span :class="{ invisible: loading }">{{ label }}</span>
    <div v-if="loading" class="spinner-container">
      <Spinner :size="24" />
    </div>
  </component>
</template>

<style scoped>
.btn {
  /* layout */
  cursor: pointer;
  display: flex;
  height: 48px;
  padding: 13px 30px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  color: #000; /* Changed text color for gold buttons */
  text-align: center;
  /* Default button */
  font-family: Arial;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px; /* 150% */
  /* style */
  border-radius: 100px;
  background: #000;
  border: none; /* Removed border */
  position: relative; /* Added for absolute positioning of spinner */
}

.btn[aria-busy='true'] {
  cursor: wait;
}

.btn.gold {
  background: #ffc627;
  color: #000; /* Set text color to black for gold buttons */
}

.btn.black {
  color: #fff;
  background: #000;
}

.btn.white {
  background: #fff;
  color: #000;
}

.btn.maroon {
  /* Added maroon button style */
  background: #8c1d40;
  color: #fff; /* Set text color to white for maroon buttons */
}

.btn:not([aria-busy='true']):not(:disabled):hover {
  opacity: 80%;
}

.btn:disabled:not([aria-busy='true']) {
  cursor: not-allowed; /* Change cursor to not-allowed when disabled */
  opacity: 0.5; /* Optional: make disabled button look less prominent */
}

.invisible {
  opacity: 0; /* Make text invisible when loading */
}

.spinner-container {
  position: absolute;
  top: 50%; /* Center vertically */
  left: 50%; /* Center horizontally */
  transform: translate(-50%, -50%); /* Adjust for the spinner's size */
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
